// src/components/layout/AdminLayout.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/apiClient';
import NotificationDropdown from '../common/NotificationDropdown';
import {
  HomeIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  CreditCardIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  UserCircleIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  LanguageIcon,
  FolderIcon,
  ComputerDesktopIcon,
  ChartBarIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

const AdminLayout = ({ children }) => {
  const { i18n, t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);

  // Badge "non lus" pour l'entrée Messages (superadmin uniquement).
  // Échec silencieux : la navigation reste utilisable sans le compteur.
  useEffect(() => {
    if (user?.role !== 'superadmin') return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await api.get('/contact');
        const list = Array.isArray(res) ? res : (res?.data || []);
        if (!cancelled && Array.isArray(list)) {
          setUnreadMessages(list.filter((m) => !m.read).length);
        }
      } catch (e) {
        // compteur optionnel : on ignore l'erreur
      }
    };
    fetchUnread();
    return () => { cancelled = true; };
  }, [user?.role, location.pathname]);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await logout();
      // Force a page reload to ensure complete state reset
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      // Force navigation to login if logout fails
      window.location.href = '/login';
    }
  };

  // Get the base path based on user role
  const getBasePath = () => {
    return user?.role === 'superadmin' ? '/superadmin' : '/admin';
  };

  const basePath = getBasePath();

  const navigationItems = [
    { name: 'Dashboard', path: `${basePath}/dashboard`, icon: HomeIcon },
    ...(user?.role === 'superadmin' ? [
      { name: 'Entreprises', path: `${basePath}/companies`, icon: BuildingOfficeIcon },
      { name: 'Analyses', path: `${basePath}/analyses`, icon: FolderIcon }
    ] : []),
    { name: 'Utilisateurs', path: `${basePath}/users`, icon: UsersIcon },
    ...(user?.role === 'admin' || user?.role === 'superadmin' ? [{ name: 'Portfolio', path: `${basePath}/portfolios`, icon: FolderIcon }] : []),
    {
      name: 'Abonnements',
      path: user?.role === 'superadmin' ? `${basePath}/subscriptions` : `${basePath}/subscription`,
      icon: CreditCardIcon
    },
    { name: "Logs d'activité", path: `${basePath}/activities`, icon: ClipboardDocumentListIcon },
    ...(user?.role === 'superadmin' ? [
      { name: 'Consommation IA', path: `${basePath}/ai-consumption`, icon: ChartBarIcon },
      { name: 'Messages', path: `${basePath}/messages`, icon: EnvelopeIcon, badge: unreadMessages }
    ] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100"
        >
          {isMobileMenuOpen ? (
            <XMarkIcon className="h-6 w-6" />
          ) : (
            <Bars3Icon className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-sm border-r border-slate-200 transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0`}
      >
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-slate-200">
            <h1 className="text-xl font-bold text-blue-600">EBIOS RM</h1>
            <p className="text-sm text-gray-600">Administration</p>
          </div>

          {/* Navigation */}
          <div className="px-4 py-2 mt-2">
            <Link
              to="/app"
              className="flex items-center px-4 py-2 rounded-xl text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors border border-blue-200"
            >
              <ComputerDesktopIcon className="h-5 w-5 mr-3" />
              <span>Retour à l'application</span>
            </Link>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-center px-4 py-3 rounded-xl transition-colors duration-200 ${isActive(item.path)
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-slate-50 hover:text-blue-600'
                  }`}
              >
                <item.icon className="h-5 w-5 mr-3" />
                <span className="flex-1">{item.name}</span>
                {item.badge > 0 && (
                  <span className="ml-2 bg-blue-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center space-x-3">
              <UserCircleIcon className="h-8 w-8 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {user?.name || 'User'}
                </p>
                <button
                  onClick={handleLogout}
                  className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  Déconnexion
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`lg:ml-64 transition-margin duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-0'}`}>
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b border-slate-200">
          <div className="px-4 py-4 flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="hidden lg:block p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              <h2 className="text-xl font-semibold text-gray-800">
                {navigationItems.find(item => isActive(item.path))?.name || 'Dashboard'}
              </h2>
            </div>

            <div className="flex items-center space-x-4">
              {/* Language Toggle Button */}
              <div className="flex items-center bg-gray-100 rounded-full p-1 transition-all duration-300">
                <button
                  onClick={() => i18n.changeLanguage('fr')}
                  className={`flex items-center px-2 py-1 text-xs rounded-full transition-all duration-300 ${i18n.language === 'fr'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                    }`}
                  title="Français"
                >
                  <span className="fi fi-fr mr-1" style={{ fontSize: '0.875rem' }}></span>
                  <span className="font-medium">FR</span>
                </button>
                <button
                  onClick={() => i18n.changeLanguage('en')}
                  className={`flex items-center px-2 py-1 text-xs rounded-full transition-all duration-300 ${i18n.language === 'en'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                    }`}
                  title="English"
                >
                  <span className="fi fi-gb mr-1" style={{ fontSize: '0.875rem' }}></span>
                  <span className="font-medium">EN</span>
                </button>
              </div>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-300 shadow-sm hover:shadow-md focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 focus:outline-none"
                title={t('common.logout') || 'Logout'}
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">{t('common.logout') || 'Logout'}</span>
              </button>

              {/* Notification Bell - Only show for SuperAdmin */}
              {user?.role === 'superadmin' && <NotificationDropdown />}
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-medium">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {children}
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-slate-200 px-6 py-4">
          <p className="text-center text-sm text-gray-600">
            EBIOS RM - Administration © {new Date().getFullYear()}
          </p>
        </footer>
      </div>
    </div>
  );
};

export default AdminLayout;